import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Loader2, UserX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const BlockedUsers = () => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const res = await axios.get(`${API}/blocked-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUsers(res.data);
    } catch (err) {
      toast.error('Kunne ikke hente blokerede brugere');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId, userName) => {
    setUnblocking(userId);
    try {
      await axios.delete(`${API}/block/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUsers(blockedUsers.filter(u => u.id !== userId));
      toast.success(`${userName} er nu afblokeret`);
    } catch (err) {
      toast.error('Kunne ikke afblokere bruger');
    } finally {
      setUnblocking(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="text-lg font-display font-semibold text-foreground">Blokerede Brugere</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : blockedUsers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <UserX className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-display font-semibold text-foreground mb-2">
              Ingen blokerede brugere
            </h3>
            <p className="text-muted-foreground">
              Du har ikke blokeret nogen endnu.
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {blockedUsers.map((user, index) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center justify-between p-4 bg-card rounded-xl border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                    {user.photos?.[0] ? (
                      <img
                        src={user.photos[0].startsWith('/api') ? `${API.replace('/api', '')}${user.photos[0]}` : user.photos[0]}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UserX className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{user.name}</p>
                    <p className="text-sm text-muted-foreground">Blokeret</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUnblock(user.id, user.name)}
                  disabled={unblocking === user.id}
                  className="rounded-full"
                >
                  {unblocking === user.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Afbloker'
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default BlockedUsers;
